// contexts/AuthContext.tsx
'use client';

import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
} from 'react';
import api, { setAccessToken } from '@/utils/api';
import { useRouter } from 'next/navigation';

type User = {
	id: string;
	name: string;
	email: string;
	role: 'customer' | 'vendor' | 'admin' | 'alumni' | 'manager';
	isDisabled?: boolean;
	warnings?: { message: string, date: string, sentBy: string }[];
	[key: string]: any;
};

let globalRefreshPromise: Promise<User | null> | null = null;

type AuthContextValue = {
	user: User | null;
	loading: boolean;
	initialized: boolean;
	login: (
		email: string,
		password: string,
		redirectTo?: string,
	) => Promise<any>;
	logout: () => Promise<void>;
	register: (payload: any) => Promise<any>;
	refreshUser: () => Promise<User | null>;
};

const AuthContext = createContext<
	AuthContextValue | undefined
>(undefined);

export function AuthProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const router = useRouter();

	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [initialized, setInitialized] =
		useState<boolean>(false);

	// refreshUser is callable anywhere
	// Module-level singleton (outside component would be better, but inside file is fine)
	const refreshUser =
		useCallback(async (): Promise<User | null> => {
			// Check global singleton
			if (globalRefreshPromise) return globalRefreshPromise;

			globalRefreshPromise = (async () => {
				try {
					// call refresh to get new access token (backend should use refresh cookie)
					const r = await api.post('/auth/refresh');
					const access = r?.data?.accessToken;
					if (access) setAccessToken(access);

				// try to fetch /auth/me or use returned user
					let resolvedUser = null;
					try {
						const me = await api.get('/auth/me');
						resolvedUser = me.data.user ?? null;
					} catch (err) {
					// if /auth/me fails but refresh returned user, use it
						resolvedUser = r?.data?.user ?? null;
					}

					if (resolvedUser) {
						setUser(resolvedUser);
						localStorage.setItem('yuni_user', JSON.stringify(resolvedUser));
					}
					return resolvedUser;
				} catch (err) {
					// not authenticated
					setUser(null);
					setAccessToken(null);
					localStorage.removeItem('yuni_user');
					return null;
				} finally {
					globalRefreshPromise = null;
				}
			})();

			return globalRefreshPromise;
		}, []);

	// initial mount & polling
	useEffect(() => {
		let mounted = true;

		(async () => {
			// Optimistic: Check local storage first
			const cached = localStorage.getItem('yuni_user');
			if (cached) {
				try {
					setUser(JSON.parse(cached));
				} catch { }
			}

			// Initial Fetch
			try {
				const u = await refreshUser();
				if (!mounted) return;
				if (!u) {
					setUser(null);
					localStorage.removeItem('yuni_user');
				}
			} finally {
				if (!mounted) return;
				setLoading(false);
				setInitialized(true);
			}
		})();

		// Polling Interval for "Live" updates (warnings, disabled status)
		const interval = setInterval(async () => {
			try {
				// Only poll if we think we are logged in, or to check session validity
				// Using refreshUser might be too heavy if it rotates tokens.
				// Let's just fetch /auth/me for lightweight status check
				// BUT we need to update global user state if it changes.
				const me = await api.get('/auth/me');
				const freshUser = me.data.user;
				if (freshUser && mounted) {
					// Update if changed (JSON stringify is a cheap deep-compare for this size)
					setUser(prev => {
						if (JSON.stringify(prev) !== JSON.stringify(freshUser)) {
							localStorage.setItem('yuni_user', JSON.stringify(freshUser));
							return freshUser;
						}
						return prev;
					});
				}
			} catch (err) {
				// If 401, we might be logged out. 
				// Let refreshUser handle clean logout/refresh logic if needed, 
				// or just ignore strictly on polling errors to avoid disruption.
				console.error('Auth polling error', err);
			}
		}, 10000); // 10 seconds

		return () => {
			mounted = false;
			clearInterval(interval);
		};
	}, [refreshUser]);

	// Redirect all users to home page
	function routeToHome() {
		router.push('/');
	}

	// login: call endpoint, set access token and user
	const login = useCallback(
		async (
			email: string,
			password: string,
			redirectTo?: string,
		) => {
			setLoading(true); // Ensure loading state during login
			try {
				const resp = await api.post('/auth/login', {
					email,
					password,
				});
				const access = resp?.data?.accessToken;
				if (access) setAccessToken(access);

				let resolvedUser: User | null = null;
				try {
					const me = await api.get('/auth/me');
					resolvedUser =
						me.data.user ?? resp?.data?.user ?? null;
				} catch {
					resolvedUser = resp?.data?.user ?? null;
				}

				if (resolvedUser) {
					console.log('AuthContext: resolvedUser:', resolvedUser); // DEBUG
					setUser(resolvedUser);
					localStorage.setItem('yuni_user', JSON.stringify(resolvedUser));
				}

				console.log('Login success, redirecting...');

				// Always redirect to home page after login
				router.push('/');

				return resp.data;
			} finally {
				setLoading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[router],
	);

	const register = useCallback(
		async (payload: any) => {
			setLoading(true);
			try {
				const resp = await api.post(
					'/auth/register',
					payload,
				);
				const access = resp?.data?.accessToken;
				if (access) setAccessToken(access);

				let resolvedUser = null;
				try {
					const me = await api.get('/auth/me');
					resolvedUser =
						me.data.user ?? resp?.data?.user ?? null;
				} catch {
					resolvedUser = resp?.data?.user ?? null;
				}

				if (resolvedUser) {
					setUser(resolvedUser);
					localStorage.setItem('yuni_user', JSON.stringify(resolvedUser));
					router.push('/');
				}

				return resp.data;
			} finally {
				setLoading(false);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[router],
	);

	const logout = useCallback(async () => {
		setLoading(true);
		try {
			await api.post('/auth/logout');
		} finally {
			setAccessToken(null);
			setUser(null);
			localStorage.removeItem('yuni_user');
			setLoading(false);
			router.push('/');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router]);

	return (
		<AuthContext.Provider
			value={{
				user,
				loading,
				initialized,
				login,
				logout,
				register,
				refreshUser,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx)
		throw new Error(
			'useAuth must be used inside AuthProvider',
		);
	return ctx;
}
